import { z } from 'zod';
import { createModel, QueryMany, QueryOne } from '../../common';
import { dateSchema, idSchema, objectIdSchema } from '../../validation';

/**
 * Where the idempotency record is from.
 *
 * - `request`   — the `Idempotency-Key` header on a controller route.
 * - `webhook`   — inbound webhook dedupe (e.g. WhatsApp Meta retries
 *                 by `externalMessageId`).
 * - `tool_call` — graph-level dedupe keyed by `(runId, toolCallId)`
 *                 inside `tool.base.ts` so re-played tool calls don't
 *                 double-execute.
 */
export const idempotencyKeyScopeSchema = z.enum(['request', 'webhook', 'tool_call']);
export type IdempotencyKeyScope = z.infer<typeof idempotencyKeyScopeSchema>;

/**
 * Pointer back to a tool result that was committed under this key —
 * used by tool-call dedupe so a replay returns the original output.
 */
export const idempotencyToolResultRefSchema = z.object({
  conversationMessageId: objectIdSchema,
  toolCallId: idSchema,
});
export type IdempotencyToolResultRef = z.infer<typeof idempotencyToolResultRefSchema>;

/**
 * `IdempotencyKey` — server-side dedupe with a 24h TTL.
 *
 * Used in three places:
 * 1. `POST /agent/messages` — frontend retries on flaky SSE.
 * 2. Inbound webhooks — Meta WhatsApp retries by `externalMessageId`.
 * 3. Tool-call replays inside `tool.base.ts` — `(runId, toolCallId)`.
 *
 * The natural primary key is the compound `(key, scope)`; that's the
 * unique index we ship. `requestHash` guards against the same key
 * being reused with a different request body.
 *
 * See `docs/ai/concurrency.md`.
 */
export const IdempotencyKeySchema = createModel({
  /**
   * The actual key. Per scope:
   * - `request`   — UUID v4 from the `Idempotency-Key` header.
   * - `webhook`   — provider message id (e.g. `wamid.HBgM…`).
   * - `tool_call` — `${runId}:${toolCallId}` (constructed inside
   *                 `tool.base.ts`).
   */
  key: idSchema.min(1),
  scope: idempotencyKeyScopeSchema,

  /**
   * Sha-256 of the canonical request body (for `request` scope) so we
   * reject reuse with a different payload.
   */
  requestHash: idSchema.optional(),

  /**
   * Cached response — for `request` scope this is the JSON body that
   * was originally returned to the client. For `tool_call` it's the
   * tool result. Replays return this verbatim.
   */
  response: z.unknown().optional(),

  toolResultRef: idempotencyToolResultRefSchema.optional(),

  /**
   * The `_id`'s timestamp drives the TTL index. We also expose
   * `createdAt` explicitly so callers don't have to peel it off
   * `_id`. The `doc.createdAt` from `createModel` covers full audit;
   * this field is just the TTL anchor consumers reach for.
   */
  createdAt: dateSchema,
});

export type IdempotencyKey = z.infer<typeof IdempotencyKeySchema.model>;
export type IdempotencyKeyData = z.infer<typeof IdempotencyKeySchema.data>;
export type IdempotencyKeyPartial = z.infer<typeof IdempotencyKeySchema.partial>;
export type IdempotencyKeyQueryOne = QueryOne<IdempotencyKey>;
export type IdempotencyKeyQueryMany = QueryMany<IdempotencyKey>;
